import { storeToRefs } from 'pinia';
import { useViewerVisualizationsStore } from '@/stores/useViewerVisualizationsStore.js';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import pLimit from 'p-limit';
import { useStore } from 'vuex';
import usePCD from '@/composables/annotationTool/usePCD.js';
import { decode } from 'fast-png';
import { createRasterCanvas, rasterToBlob } from '@/utils/raster';
import { shallowRef, nextTick } from 'vue';
import useProjection from '@/composables/annotationTool/useProjection.js';
import * as THREE from 'three';
import { WorkerPool } from '@/workers/workerPool.js';
import { useThrottleFn } from '@vueuse/core';
import { useEditorStore } from '@/stores/useEditorStore.js';

export default function useViewerVisualizations() {
  const cacheWorkerPool = new WorkerPool('@/workers/cacheWorker.js');
  const store = useStore();
  const max_concurrent_req = 6;
  const limit = pLimit(max_concurrent_req);
  const bufferLength = 300;
  const backCache = 20;
  const cacheTypes = ['pcd'];
  const blobCacheTypes = ['depthmap'];
  const arrayBufferCacheTypes = ['lidar'];
  const fpsSkipMap = {
    1: { fps: 1, skip: 0 },
    5: { fps: 5, skip: 0 },
    10: { fps: 10, skip: 0 },
    20: { fps: 10, skip: 1 },
    100: { fps: 10, skip: 10 },
  };

  const editorStore = useEditorStore();
  const {
    currentSample,
    changedAnnotations,
  } = storeToRefs(editorStore);

  const visualizationStore = useViewerVisualizationsStore();
  const {
    samples,
    animationImageCache,
    frame,
    playing,
    buffered,
    keyframes,
    doKeyframesExist,
    keyFramesEnabled,
    frameCount,
    fps,
    isInitializingAnimation,
    abortControllerForCaching,
    topics,
    limitAnnotationsToSet,
  } = storeToRefs(visualizationStore);
  const {
    pauseAnimation,
    addNewSampleToCache,
    resetForNewSequence,
    getAnimationDelay,
    $reset,
  } = visualizationStore;

  const {
    parsePCDFile, parsePCDFileAsPointsBuffer, colorPointsByXValue, setPointsMaterial,
  } = usePCD();

  const {
    drawPCDPoints,
  } = useProjection();

  function getAnnotationResponseDataType(annotation) {
    if (cacheTypes.includes(annotation.type)) {
      return 'text';
    }
    if (blobCacheTypes.includes(annotation.type)) {
      return 'blob';
    }
    if (arrayBufferCacheTypes.includes(annotation.type)) {
      return 'arrayBuffer';
    }
    return 'text';
  }

  async function cacheImages() {
    cacheWorkerPool.clearQueue();

    // Determine the range to cache
    const lower = Math.max(0, frame.value - 1 - backCache);
    const upper = Math.min(frame.value - 1 + bufferLength, samples.value.length - 1);
    const temp = {};

    // Clear the unloaded cache and the out of range cache
    for (let key = lower; key <= upper; key++) {
      if (key in animationImageCache.value && animationImageCache.value[key]?.loaded) {
        temp[key] = animationImageCache.value[key];
      }
    }

    // animationImageCache now just has the cached images within the range
    animationImageCache.value = temp;

    // Cache the missing images in the range
    for (let i = lower; i <= upper; i++) {
      if (!animationImageCache.value[i]) {
        addNewSampleToCache(i);
        cacheWorkerPool.addTask({ index: i, imageId: samples.value[i], token: store.state.user.token });
      }
    }
  }

  async function fetchImageObjAndAnnotations(imageId) {
    return new Promise(async (resolve, reject) => {
      if (!imageId) {
        reject(new Error("Missing Image Id"));
      }

      const dataConnect = new DatastoreConnect();
      dataConnect.getImage(
        {
          id: imageId,
          get_annotations: true,
        },
      )
        .then((resp) => {
          if (resp.error !== undefined) {
            throw new Error(`Error loading image object`);
          }
          resolve(resp.result);
        })
        .catch((e) => {
          console.log(e);
          reject(e);
        });
    });
  }

  // async function fetchAnnotations(sample, index) {
  //   return new Promise((resolve, reject) => {
  //     const dataConnect = new DatastoreConnect();
  //     try {
  //       dataConnect.getAnnotations(
  //         {
  //           image_id: sample.id,
  //           annotation_set_id: selectedAnnotationSets.value,
  //         },
  //       ).then((data) => {
  //         if (!data.result || data.error) {
  //           if (animationImageCache.value[index] && animationImageCache.value[index].annotations) {
  //             animationImageCache.value[index].annotations = [];
  //             resolve([]);
  //           } else {
  //             // reject(new Error(`Sample value ${index} does not exist. Can not set value "annotations"`));
  //             resolve(null);
  //           }
  //         } else {
  //           if (animationImageCache.value[index] && animationImageCache.value[index].annotations) {
  //             animationImageCache.value[index].annotations = data.result;
  //             resolve(data.result);
  //           } else {
  //             resolve(null);
  //             // reject(new Error(`Sample value ${index} does not exist. Can not set value "annotations"`));
  //           }
  //         }
  //       }).catch((e) => {
  //         reject(e);
  //       });
  //     } catch (error) {
  //       reject(error);
  //     }
  //   });
  // }

  async function loadSample(imageId, localSignal) {
    return new Promise((resolve, reject) => {
      if (localSignal && localSignal.aborted) {
        reject(new Error(`Loading sample aborted`));
      }

      loadImage(imageId)
        .then(async (image) => {
          if (localSignal && localSignal.aborted) {
            reject(new Error(`Loading sample aborted`));
          }
          currentSample.value.image = image;
          const imageObjAndAnnotations = await fetchImageObjAndAnnotations(imageId);
          const cacheEntry = await loadAnnotationUrls(image, imageId, null, imageObjAndAnnotations.annotations, localSignal);
          cacheEntry.image = image;
          cacheEntry.imageObj = imageObjAndAnnotations;
          cacheEntry.annotations = imageObjAndAnnotations.annotations;
          return cacheEntry;
        })
        .then((cacheEntry) => {
          if (localSignal && localSignal.aborted) {
            reject(new Error(`Loading sample aborted`));
          } else {
            currentSample.value = {
              ...currentSample.value,
              ...cacheEntry,
              loaded: true,
            };
          }
          resolve();
        })
        .catch((e) => {
          if (localSignal && localSignal.aborted) {
            reject(new Error(`Loading sample aborted`));
          }
          // ignore errors
          console.warn(e, `Skipped frame`);
          currentSample.value.failed = true;
          currentSample.value.loaded = true;
          resolve();
        });
    });
  }

  async function parseAnnotation(annotationCacheEntry, image, annotation, localSignal) {
    if (annotation.type === 'pcd' && annotation.data) {
      const pointsBuffer = shallowRef(null);
      pointsBuffer.value = parsePCDFileAsPointsBuffer(annotation.data);
      setPointsMaterial(pointsBuffer.value.material);
      annotationCacheEntry.pcd = annotationCacheEntry.pcd || [];
      annotationCacheEntry.pcd.push({
        annotation,
        pointsBuffer,
      });
      if (image) {
        const projectionImage = await drawPCDPoints(pointsBuffer.value, image.width, image.height, { radius: 4 });
        annotationCacheEntry.pcdProjections = annotationCacheEntry.pcdProjections || [];
        annotationCacheEntry.pcdProjections.push(projectionImage);
      }
    }
    if (annotation.type === 'depthmap' && annotation.data) {
      const resultImage = await convertBlobToGradientImage(annotation.data, localSignal);
      annotationCacheEntry.depthmap = annotationCacheEntry.depthmap || [];
      annotationCacheEntry.depthmap.push(resultImage);
    }
    if (annotation.type === 'lidar' && annotation.data) {
      const pointsBuffer = shallowRef(null);
      pointsBuffer.value = parsePCDFileAsPointsBuffer(annotation.data);
      colorPointsByXValue(pointsBuffer.value);
      annotationCacheEntry.lidar = annotationCacheEntry.lidar || [];
      annotationCacheEntry.lidar.push({
        annotation,
        pointsBuffer,
      });
      if (image) {
        const projectionImage = await drawPCDPoints(pointsBuffer.value, image.width, image.height);
        annotationCacheEntry.lidarProjections = annotationCacheEntry.lidarProjections || [];
        annotationCacheEntry.lidarProjections.push(projectionImage);
      }
    }
    return annotationCacheEntry;
  }

  async function loadAnnotationUrls(image, image_id, sampleIndex, annotations, localSignal) {
    if (localSignal && localSignal.aborted) {
      Promise.reject(new Error(`Loading sample ${sampleIndex} aborted`));
    }
    if (image_id && annotations) {
      const annotationPromises = [];
      const dataConnect = new DatastoreConnect();
      const annotationCacheEntry = {};
      annotations.forEach((anno) => {
        if (anno.is_sensor && anno.url) {
          annotationPromises.push(
            new Promise(async (resolve, reject) => {
              try {
                const data = await dataConnect.getSensorFile(anno.id, getAnnotationResponseDataType(anno), null);
                anno.data = data;
                await parseAnnotation(annotationCacheEntry, image, anno, localSignal);
                resolve();
              } catch (error) {
                reject(error);
              }
            }),
          );
        } else if (anno.data_url) {
          annotationPromises.push(
            new Promise(async (resolve, reject) => {
              const data = await dataConnect.getAnnotationFile(anno.id, getAnnotationResponseDataType(anno), null);
              anno.data = data;
              await parseAnnotation(annotationCacheEntry, image, anno, localSignal);
              resolve();
            }),
          );
        } else if (anno.polygon && cacheTypes.includes(anno.type)) {
          annotationPromises.push(
            new Promise(async (resolve, reject) => {
              const textEncoder = new TextEncoder();
              const arrayBuffer = textEncoder.encode(anno.polygon).buffer;
              anno.data = arrayBuffer;
              await parseAnnotation(annotationCacheEntry, image, anno, localSignal);
              resolve();
            }),
          );
        }
      });
      const results = await Promise.allSettled(annotationPromises);
      const allSuccess = results.every((result) => result.status === 'fulfilled');
      if (!allSuccess) {
        if (sampleIndex) {
          Promise.reject(new Error(`Load annotations for sample ${sampleIndex} missing data`));
        } else {
          Promise.reject(new Error(`Load annotations for current sample missing data`));
        }
      }
      return annotationCacheEntry;
    }
    if (sampleIndex) {
      return Promise.reject(new Error(`Load annotations for sample ${sampleIndex} missing data`));
    } else {
      return Promise.reject(new Error(`Load annotations for current sample missing data`));
    }
  }

  async function loadSampleToCache(imageId, index, localSignal) {
    return new Promise((resolve, reject) => {
      if (localSignal && localSignal.aborted) {
        reject(new Error(`Loading sample ${index} aborted`));
      }
      animationImageCache.value[index].loading = true;

      loadImage(imageId, index)
        .then(async (image) => {
          if (localSignal && localSignal.aborted) {
            reject(new Error(`Loading sample ${index} aborted`));
          } else {
            const imageObjAndAnnotations = await fetchImageObjAndAnnotations(imageId);
            const cacheEntry = await loadAnnotationUrls(image, imageId, index, imageObjAndAnnotations.annotations, localSignal);
            cacheEntry.image = image;
            cacheEntry.imageObj = imageObjAndAnnotations;
            cacheEntry.annotations = imageObjAndAnnotations.annotations;
            return cacheEntry;
          }
        })
        .then((cacheEntry) => {
          if (localSignal && localSignal.aborted) {
            reject(new Error(`Loading sample aborted`));
          } else {
            if (animationImageCache.value && animationImageCache.value[index]) {
              animationImageCache.value[index] = {
                ...animationImageCache.value[index],
                ...cacheEntry,
                loaded: true,
              };
            }
          }
          resolve();
        })
        .catch((e) => {
          // ignore errors
          console.log(e, `Skipped frame`);
          if (localSignal && localSignal.aborted) {
            reject(new Error(`Loading sample aborted`));
          } else {
            if (animationImageCache.value && animationImageCache.value[index]) {
              animationImageCache.value[index].failed = true;
              animationImageCache.value[index].loaded = true;
              animationImageCache.value[index].loading = false;
            }
            resolve();
          }
        });
    });
  }

  function loadImage(image_id) {
    if (image_id) {
      return new Promise(async (resolve, reject) => {
        await fetch(`image/redirect/${image_id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${store.state.user.token}`,
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Error loading image`);
            }
            return response.blob();
          })
          .then((blob) => {
            const image = document.createElement('img');
            image.onerror = (e) => {
              reject(e);
            };
            image.onload = () => {
              // Add review status to annotation if it exists
              // if (imageObj.review_status === 'Done' && imageObj.annotations) {
              //   imageObj.annotations.forEach((anno) => {
              //     anno.reviewStatus = 'verified';
              //   });
              // }

              resolve(image);
            };
            image.src = URL.createObjectURL(blob);
          })
          .catch((e) => {
            console.log(e);
            reject();
          });
      });
    }
  }

  async function startAnimation() {
    playing.value = true;
    const delay = (ms) => new Promise((resolve) => {
      setTimeout(() => resolve(), ms);
    }, ms);

    // const lastFrame = Math.max(0, Math.max(...Object.keys(animationImageCache.value).map((e) => parseInt(e))));

    if (frame.value === frameCount.value) {
      frame.value = 1;
    }

    while (true) {
      // Break if paused
      if (!playing.value) {
        break;
      }
      if (frame.value + 1 <= frameCount.value + 1 && animationImageCache.value?.[frame.value]?.loaded) {
        let nextFrame = frame.value + 1 + fpsSkipMap[fps.value].skip;
        if (nextFrame > frameCount.value) {
          nextFrame = frameCount.value;
        }
        frame.value = nextFrame;
        updateSample();
        nextTick(() => {
          cacheImages();
        });
      }
      await delay(getAnimationDelay(fpsSkipMap[fps.value].fps));
      if (frame.value === frameCount.value) {
        break;
      }
    }
    playing.value = false;
  }

  async function convertBlobToGradientImage(uint16Array, localSignal) {
    try {
    // Convert the uint16Array to an ArrayBuffer
      const arrayBuffer = await uint16Array.arrayBuffer();

      // Decode the image
      const decodedImage = await decode(arrayBuffer);
      const depthData16Bit = decodedImage.data; // Read as 16-bit data
      const width = decodedImage.width;
      const height = decodedImage.height;

      // Calculate min and max depth values
      let minDepthValue = Infinity;
      let maxDepthValue = -Infinity;

      for (let i = 0; i < depthData16Bit.length; i++) {
        const value = depthData16Bit[i];
        minDepthValue = Math.min(minDepthValue, value);
        maxDepthValue = Math.max(maxDepthValue, value);
      }

      // Portable raster surface (OffscreenCanvas → <canvas> fallback). Mirrors
      // the null-safe pattern in cacheWorker.js for consistency/defensiveness.
      const offscreenCanvas = createRasterCanvas(width, height);
      const ctx = offscreenCanvas?.getContext('2d');

      // Create the RGBA buffer (from the canvas ctx when present, else a plain buffer).
      const imageData = ctx
        ? ctx.createImageData(width, height)
        : { data: new Uint8ClampedArray(width * height * 4), width, height };
      const data = imageData.data;

      // Process each 16-bit depth value and map it to red-blue
      for (let i = 0; i < depthData16Bit.length; i++) {
        const depth16Bit = depthData16Bit[i];

        // Normalize depth to a range [0, 1]
        const normalizedDepth = (depth16Bit - minDepthValue) / (maxDepthValue - minDepthValue);

        // Scale normalized depth to [0, 255]
        const scaledDepth = Math.floor(normalizedDepth * 255);

        // Map normalized grayscale to red-blue gradient
        const red = Math.min(255, 2 * scaledDepth); // Red increases as depth decreases
        const blue = Math.min(255, 2 * (255 - scaledDepth)); // Blue increases as depth increases
        const green = 0; // No green in the gradient

        // Set pixel color in the image data
        const pixelIndex = i * 4;
        data[pixelIndex] = red; // Red
        data[pixelIndex + 1] = green; // Green
        data[pixelIndex + 2] = blue; // Blue
        data[pixelIndex + 3] = 255; // Full opacity
      }

      // Put the modified pixel data back onto the canvas (skipped when no canvas)
      if (ctx) ctx.putImageData(imageData, 0, 0);

      // Convert the canvas to a blob (portable across OffscreenCanvas / <canvas>)
      const newBlob = await rasterToBlob(offscreenCanvas, { width, height, imageData });

      // Create an image from the blob
      return await new Promise((resolve, reject) => {
        const resultImage = new Image();
        resultImage.src = URL.createObjectURL(newBlob);

        resultImage.onload = () => {
          if (localSignal && localSignal.aborted) {
            console.error(`Loading sample aborted`);
            reject(new Error(`Loading sample aborted`));
          } else {
            resolve(resultImage);
          }
        };

        resultImage.onerror = (e) => {
          reject(new Error(`Error loading image: ${e.message}`));
        };
      });
    } catch (error) {
      console.error(`Failed to convert blob to gradient image: ${error.message}`);
      throw error;
    }
  }

  // async function convertBlobToGradientImage(uint16Array, sampleIndex, localSignal) {
  //   const reader = new FileReader();

  //   const arrayBuffer = await uint16Array.arrayBuffer();
  //   const decodedImage = await decode(arrayBuffer);

  //   reader.onload = () => {
  //     const depthData16Bit = decodedImage.data; // Read as 16-bit data
  //     const width = decodedImage.width;
  //     const height = decodedImage.height;
  //     let minDepthValue = Infinity;
  //     let maxDepthValue = -Infinity;

  //     for (let i = 0; i < depthData16Bit.length; i++) {
  //       const value = depthData16Bit[i];

  //       // Update min and max values
  //       if (value < minDepthValue) {
  //         minDepthValue = value;
  //       }
  //       if (value > maxDepthValue) {
  //         maxDepthValue = value;
  //       }
  //     }
  //     // Create OffscreenCanvas
  //     const offscreenCanvas = new OffscreenCanvas(width, height);
  //     const ctx = offscreenCanvas.getContext('2d');

  //     // Create ImageData object for the OffscreenCanvas
  //     const imageData = ctx.createImageData(width, height);
  //     const data = imageData.data;

  //     // Process each 16-bit depth value and map it to red-blue
  //     for (let i = 0; i < depthData16Bit.length; i++) {
  //       const depth16Bit = depthData16Bit[i];

  //       // Normalize depth to a range [0, 1]
  //       const normalizedDepth = (depth16Bit - minDepthValue) / (maxDepthValue - minDepthValue);

  //       // Scale normalized depth to [0, 255]
  //       const scaledDepth = Math.floor(normalizedDepth * 255);

  //       // Map normalized grayscale to red-blue gradient
  //       const red = Math.min(255, 2 * scaledDepth); // Red increases as depth decreases
  //       const blue = Math.min(255, 2 * (255 - scaledDepth)); // Blue increases as depth increases
  //       const green = 0; // No green in the gradient

  //       // Set pixel color in the image data
  //       const pixelIndex = i * 4;
  //       data[pixelIndex] = red; // Red
  //       data[pixelIndex + 1] = green; // Green
  //       data[pixelIndex + 2] = blue; // Blue
  //       data[pixelIndex + 3] = 255; // Full opacity
  //     }

  //     // Put the modified pixel data back onto the OffscreenCanvas
  //     ctx.putImageData(imageData, 0, 0);

  //     // Convert the OffscreenCanvas to a blob and create an image
  //     offscreenCanvas.convertToBlob().then((newBlob) => {
  //       const resultImage = new Image();
  //       resultImage.src = URL.createObjectURL(newBlob);
  //       resultImage.onload = () => {
  //         if (localSignal && localSignal.aborted) {
  //           console.error(`Loading sample ${sampleIndex} aborted`);
  //           return;
  //         }
  //         // animationImageCache.value[sampleIndex].depthmap = animationImageCache.value[sampleIndex].depthmap || [];
  //         // animationImageCache.value[sampleIndex].depthmap.push(resultImage);
  //         resolve(resultImage);
  //       };
  //       resultImage.onerror = (e) => { console.log(e); };
  //     });
  //   };

  //   reader.readAsArrayBuffer(uint16Array);
  // }

  // const worker = new Worker(new URL('@/workers/cacheWorker.js', import.meta.url), {
  //   type: 'module',
  // });

  async function handleImageBlob(blob) {
    return new Promise((resolve) => {
      const imgElement = document.createElement("img");
      imgElement.onload = () => resolve(imgElement);
      imgElement.src = URL.createObjectURL(blob);
    });
  }

  cacheWorkerPool.onmessage((message) => {
    if (message.type === 'started') {
      // console.log(`Worker started processing task:`, message.data);
      handleCacheWorkerStarted(message.data);
    } else if (message.type === 'result') {
      // console.log(`Worker completed task:`, message.data);
      handleCacheWorkerResults(message.data);
    }
  });

  async function handleCacheWorkerStarted(data) {
    animationImageCache.value[data.index].loading = true;
  }

  async function handleCacheWorkerResults(data) {
    if (data.error) {
      if (data.index !== undefined && data.index !== null) {
        animationImageCache.value[data.index].failed = true;
      } else {
        currentSample.value.failed = true;
      }
    }

    const imageBlob = data.imageBlob;
    let imgElement = null;
    if (imageBlob) {
      imgElement = await handleImageBlob(imageBlob);
    }

    const parsedAnnotations = {};

    // const image = document.createElement('img');
    // image.onerror = (e) => {
    //   console.log(e);
    // };
    // image.onload = () => {
    if (data.lidar) {
      // Reconstruct lidar
      const lidar = data.lidar;
      lidar.forEach((item) => {
        const { annotation, positions, colors } = item;

        // Reconstruct the geometry
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

        if (colors) {
          geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        }

        geometry.computeBoundingSphere();
        geometry.computeBoundingBox();

        // Create a material for the points (you can customize it as needed)
        const material = new THREE.PointsMaterial({ size: 0.1, vertexColors: true });

        // Reconstruct the THREE.Points object
        const points = new THREE.Points(geometry, material);

        const pointsBuffer = shallowRef(null);
        pointsBuffer.value = points;
        item.pointsBuffer = pointsBuffer;
      });

      // if (data.index) {
      //   if (animationImageCache.value[data.index]) {
      //     animationImageCache.value[data.index].lidar = lidar;
      //   }
      // } else {
      //   currentSample.value.lidar = lidar;
      // }

      parsedAnnotations.lidar = lidar;
    }

    if (data.pcd) {
      // Reconstruct pcd
      const pcd = data.pcd;
      pcd.forEach((item) => {
        const { annotation, positions } = item;

        // Reconstruct the geometry
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

        geometry.computeBoundingSphere();
        geometry.computeBoundingBox();

        // Create a material for the points (you can customize it as needed)
        const material = new THREE.PointsMaterial();
        setPointsMaterial(material);

        // Reconstruct the THREE.Points object
        const points = new THREE.Points(geometry, material);

        const pointsBuffer = shallowRef(null);
        pointsBuffer.value = points;
        item.pointsBuffer = pointsBuffer;
      });

      // if (data.index) {
      //   if (animationImageCache.value[data.index]) {
      //     animationImageCache.value[data.index].pcd = pcd;
      //   }
      // } else {
      //   currentSample.value.pcd = pcd;
      // }

      parsedAnnotations.pcd = pcd;
    }

    if (data.pcdProjectionBlobs) {
      const pcdProjectionBlobs = data.pcdProjectionBlobs;
      const pcdProjections = [];
      pcdProjectionBlobs.forEach(async (item) => {
        const imgElement = await handleImageBlob(item);
        pcdProjections.push(imgElement);
      });
      parsedAnnotations.pcdProjections = pcdProjections;
    }

    if (data.pcdProjectionBlobs) {
      const pcdProjectionBlobs = data.pcdProjectionBlobs;
      const pcdProjections = [];
      pcdProjectionBlobs.forEach(async (item) => {
        const imgElement = await handleImageBlob(item);
        pcdProjections.push(imgElement);
      });
      parsedAnnotations.pcdProjections = pcdProjections;
    }

    if (data.lidarProjectionBlobs) {
      const lidarProjectionBlobs = data.lidarProjectionBlobs;
      const lidarProjections = [];
      lidarProjectionBlobs.forEach(async (item) => {
        const imgElement = await handleImageBlob(item);
        lidarProjections.push(imgElement);
      });
      parsedAnnotations.lidarProjections = lidarProjections;
    }

    if (data.depthmapBlobs) {
      const depthmapBlobs = data.depthmapBlobs;
      const depthmap = [];
      depthmapBlobs.forEach(async (item) => {
        const imgElement = await handleImageBlob(item);
        depthmap.push(imgElement);
      });
      parsedAnnotations.depthmap = depthmap;
    }

    if (data.index !== undefined && data.index !== null) {
      if (animationImageCache.value[data.index]) {
        animationImageCache.value[data.index].image = imgElement;
        animationImageCache.value[data.index].imageObj = data.imageObj;
        animationImageCache.value[data.index].annotations = data.annotations ?? [];
        // Dynamically append annotations
        for (const [key, value] of Object.entries(parsedAnnotations)) {
          animationImageCache.value[data.index][key] = value;
        }
        animationImageCache.value[data.index].loaded = true;
      }
    } else {
      currentSample.value.image = imgElement;
      currentSample.value.imageObj = data.imageObj;
      currentSample.value.annotations = data.annotations ?? [];
      // Dynamically append annotations
      for (const [key, value] of Object.entries(parsedAnnotations)) {
        currentSample.value[key] = value;
      }
      currentSample.value.loaded = true;
    }
    // };
    // image.src = URL.createObjectURL(imageBlob);
  }

  function processSample({ imageId, annotationSetId, indexToCache }) {
    return new Promise((resolve, reject) => {
      const worker = new Worker(new URL('@/workers/cacheWorker.js', import.meta.url), {
        type: 'module',
      });

      worker.onmessage = async (message) => {
        if (indexToCache !== undefined && indexToCache !== null && message.data) {
          message.data.index = indexToCache;
        }
        await handleCacheWorkerResults(message.data);
        worker.terminate();
        resolve(message); // Resolve the promise with worker response
      };

      worker.onerror = (message) => {
        console.log("worker error:", message);
        worker.terminate();
        reject(message);
      };

      worker.postMessage({ imageId, token: store.state.user.token, annotationSetId });
    });
  }

  function abortCaching() {
    cacheWorkerPool.clearQueue();
    cacheWorkerPool.terminate();
  }

  async function recacheAndSetCurrentSample() {
    const currentFrame = frame.value;
    addNewSampleToCache(currentFrame - 1);
    currentSample.value.loaded = false;
    cacheWorkerPool.restartWorkers();
    await processSample({ imageId: samples.value[currentFrame - 1], annotatationSetId: limitAnnotationsToSet.value, indexToCache: currentFrame - 1 });
    if (currentFrame === frame.value) {
      updateSample();
    }
    nextTick(() => {
      cacheImages();
    });
  }

  function updateSample() {
    if (animationImageCache.value[frame.value - 1]) {
      currentSample.value = animationImageCache.value[frame.value - 1];
    }
  }

  const throttledUpdateSample = useThrottleFn(() => {
    if (animationImageCache.value[frame.value - 1]) {
      currentSample.value = animationImageCache.value[frame.value - 1];

      //Add missing annotations from changed annotations
      for (const [c_ann_id, c_ann] of Object.entries(changedAnnotations.value)) {
        if( c_ann.image_id==currentSample.value.imageObj.id) {
          let exists=false;
          for( let k=0;k<currentSample.value.annotations.length;k++) {
            const s_ann = currentSample.value.annotations[k];
            if (s_ann.id==c_ann_id) {
              exists=true;
            }
          }
          if (!exists) {
            currentSample.value.annotations.push(c_ann);
          }
        }
        
      }



    }
  }, 100);

  function removeAnnotationsFromCache(annotations) {
    // Check each cache element for excluded annotations and remove them
    const excludeIds = annotations.map((anno) => anno.id);
    animationImageCache.value.forEach((imageCache, i) => {
      const excluded = imageCache.annotations.filter((annotation) => excludeIds.includes(annotation.id));
      if (excluded.length > 0) {
        // Clear the parsed annotations
        excluded.forEach((excludedAnno) => {
          animationImageCache.value[i][excludedAnno.type] = [];
        });
        // Clear annotation object
        animationImageCache.value[i].annotations = imageCache.annotations.filter((annotation) => !excludeIds.includes(annotation.id));
        animationImageCache.value[i].imageObj.annotations = imageCache.imageObj.annotations.filter((annotation) => !excludeIds.includes(annotation.id));
      }
    });
    updateSample();
  }

  function addNewAnnotationsArrayToCache(newAnnotations) {
    const groupedNewAnnotations = Object.groupBy(newAnnotations, ({ image_id }) => image_id);
    Object.values(animationImageCache.value).forEach((cacheFrame) => {
      if (cacheFrame.imageObj && groupedNewAnnotations[cacheFrame.imageObj.id]) {
        groupedNewAnnotations[cacheFrame.imageObj.id].forEach((anno) => { anno.reviewStatus = "verified"; });
        cacheFrame.annotations = [...cacheFrame.annotations, ...groupedNewAnnotations[cacheFrame.imageObj.id]];
        cacheFrame.imageObj.annotations = [...cacheFrame.imageObj.annotations, ...groupedNewAnnotations[cacheFrame.imageObj.id]];
      }
    });
    updateSample();
  }

  return {
    samples,
    animationImageCache,
    frame,
    playing,
    buffered,
    currentSample,
    keyframes,
    doKeyframesExist,
    keyFramesEnabled,
    frameCount,
    fps,
    isInitializingAnimation,
    topics,
    cacheWorkerPool,

    cacheImages,
    handleCacheWorkerStarted,
    handleCacheWorkerResults,
    processSample,
    recacheAndSetCurrentSample,
    // fetchAnnotations,
    loadSample,
    loadSampleToCache,
    loadImage,
    abortCaching,

    startAnimation,
    pauseAnimation,
    addNewSampleToCache,
    updateSample,
    throttledUpdateSample,
    removeAnnotationsFromCache,
    addNewAnnotationsArrayToCache,
    resetForNewSequence,
    $reset,
    limitAnnotationsToSet,
  };
}
